<template>
  <UsuariosExtLayout
    title="Consultar solicitudes"
    subtitle="Sigue el estado de tus trámites"
    description="Ingresa el número de solicitud que recibiste al crear el trámite. Completa la verificación y consulta el estado en línea."
    :show-back="true"
    narrow
  >
    <div class="conecta-form-card consulta-card">
      <h2 class="conecta-form-card__title">
        <i class="fas fa-search mr-2 text-success"></i>
        Buscar solicitud
      </h2>
      <p class="conecta-form-card__subtitle">
        El número aparece en el comprobante o en el correo de confirmación.
      </p>

      <form @submit.prevent="consultar">
        <div class="mb-3">
          <label for="numeroSolicitud" class="conecta-form-label">
            <i class="fas fa-hashtag"></i>
            Número de solicitud <span class="text-danger">*</span>
          </label>
          <input
            id="numeroSolicitud"
            type="text"
            inputmode="numeric"
            class="form-control conecta-form-control consulta-card__input"
            v-model="numeroSolicitud"
            :class="{ 'is-invalid': errors.numeroSolicitud }"
            placeholder="Ejemplo: 1831"
            autocomplete="off"
            maxlength="12"
            @input="soloNumeros"
          />
          <small class="conecta-form-hint d-block">
            <i class="fas fa-info-circle mr-1"></i>
            Solo números. No compartas este código con terceros.
          </small>
          <div v-if="errors.numeroSolicitud" class="conecta-invalid-feedback">
            <i class="fas fa-exclamation-circle mr-1"></i>
            {{ errors.numeroSolicitud }}
          </div>
        </div>

        <div class="mb-3 mt-4">
          <div class="d-flex justify-content-center">
            <div id="recaptcha-container-consulta"></div>
          </div>
          <div v-if="errors.recaptcha" class="conecta-invalid-feedback text-center mt-2">
            <i class="fas fa-exclamation-circle mr-1"></i>
            {{ errors.recaptcha }}
          </div>
        </div>

        <div class="consulta-card__actions">
          <button
            type="submit"
            class="btn conecta-btn-primary px-4 py-2"
            :disabled="consultando"
          >
            <i v-if="consultando" class="fas fa-spinner fa-spin mr-2"></i>
            <i v-else class="fas fa-clipboard-check mr-2"></i>
            {{ consultando ? "Consultando..." : "Consultar estado" }}
          </button>
          <button
            type="button"
            class="btn conecta-btn-outline px-4 py-2"
            :disabled="consultando"
            @click="limpiar"
          >
            <i class="fas fa-undo mr-2"></i>
            Limpiar
          </button>
        </div>
      </form>
    </div>

    <div v-if="errorConsulta" class="consulta-alert consulta-alert--error mt-3">
      <i class="fas fa-exclamation-triangle"></i>
      <div>
        <strong>No se pudo consultar</strong>
        <p>{{ errorConsulta }}</p>
      </div>
    </div>

    <div v-if="solicitud" class="consulta-resultado mt-3">
      <div class="consulta-status" :class="'consulta-status--' + estadoClase">
        <div class="consulta-status__icon">
          <i :class="estadoIcono"></i>
        </div>
        <div class="consulta-status__body">
          <span class="consulta-status__kicker">Solicitud #{{ solicitud.id }}</span>
          <h3 class="consulta-status__title">{{ labelEstado }}</h3>
          <p class="consulta-status__meta">
            {{ labelTipo }}
            <span v-if="fechaSolicitud"> · Registrada el {{ fechaSolicitud }}</span>
          </p>
        </div>
      </div>

      <ol class="consulta-steps" aria-label="Progreso de la solicitud">
        <li
          v-for="paso in pasosEstado"
          :key="paso.id"
          class="consulta-steps__item"
          :class="{
            'is-done': paso.done,
            'is-current': paso.current,
            'is-rejected': paso.rejected,
          }"
        >
          <span class="consulta-steps__dot">
            <i :class="paso.icon"></i>
          </span>
          <span class="consulta-steps__label">{{ paso.label }}</span>
        </li>
      </ol>

      <div class="conecta-form-card consulta-detalle">
        <h3 class="consulta-detalle__title">
          <i class="fas fa-id-card mr-2"></i>
          Datos de la solicitud
        </h3>
        <div class="consulta-grid">
          <div class="consulta-field">
            <span class="consulta-field__label">Tipo de trámite</span>
            <span class="consulta-field__value">{{ labelTipo }}</span>
          </div>
          <div class="consulta-field">
            <span class="consulta-field__label">Titular</span>
            <span class="consulta-field__value">{{ nombreCompleto }}</span>
          </div>
          <div class="consulta-field">
            <span class="consulta-field__label">Documento</span>
            <span class="consulta-field__value">{{ documentoMostrado }}</span>
          </div>
          <div class="consulta-field">
            <span class="consulta-field__label">Municipio</span>
            <span class="consulta-field__value">{{ ciudadNombre }}</span>
          </div>
          <div class="consulta-field">
            <span class="consulta-field__label">Correo</span>
            <span class="consulta-field__value">{{ solicitud.email || "—" }}</span>
          </div>
          <div class="consulta-field">
            <span class="consulta-field__label">Teléfono</span>
            <span class="consulta-field__value">{{ solicitud.telefono || "—" }}</span>
          </div>
          <div class="consulta-field consulta-field--full">
            <span class="consulta-field__label">Dirección</span>
            <span class="consulta-field__value">{{ solicitud.direccion || "—" }}</span>
          </div>
          <div class="consulta-field" v-if="tarjetaExterno">
            <span class="consulta-field__label">Tarjeta</span>
            <span class="consulta-field__value">{{ tarjetaExterno }}</span>
          </div>
          <div class="consulta-field" v-if="solicitud.folio_nrtec">
            <span class="consulta-field__label">Folio NRTEC</span>
            <span class="consulta-field__value">{{ solicitud.folio_nrtec }}</span>
          </div>
          <div class="consulta-field" v-if="solicitud.monto != null && solicitud.monto !== ''">
            <span class="consulta-field__label">Monto</span>
            <span class="consulta-field__value">{{ formatoPesos(solicitud.monto) }}</span>
          </div>
          <div class="consulta-field consulta-field--full" v-if="observacionTexto">
            <span class="consulta-field__label">Observación</span>
            <span class="consulta-field__value">{{ observacionTexto }}</span>
          </div>
        </div>
      </div>

      <div v-if="venta" class="conecta-form-card consulta-detalle">
        <h3 class="consulta-detalle__title">
          <i class="fas fa-receipt mr-2"></i>
          Información de pago
        </h3>
        <div class="consulta-grid">
          <div class="consulta-field">
            <span class="consulta-field__label">Estado del pago</span>
            <span class="consulta-field__value">{{ labelEstadoVenta }}</span>
          </div>
          <div class="consulta-field">
            <span class="consulta-field__label">Tipo de entrega</span>
            <span class="consulta-field__value">{{ labelTipoEntrega }}</span>
          </div>
          <div class="consulta-field">
            <span class="consulta-field__label">Total</span>
            <span class="consulta-field__value">{{ formatoPesos(venta.total) }}</span>
          </div>
          <div class="consulta-field">
            <span class="consulta-field__label">Recarga inicial</span>
            <span class="consulta-field__value">
              {{ formatoPesos(venta.valor_recarga_inicial) }}
            </span>
          </div>
          <div class="consulta-field consulta-field--full" v-if="detalleEntrega">
            <span class="consulta-field__label">Detalle de entrega</span>
            <span class="consulta-field__value">{{ detalleEntrega }}</span>
          </div>
        </div>
      </div>

      <div class="conecta-form-card consulta-detalle">
        <h3 class="consulta-detalle__title">
          <i class="fas fa-history mr-2"></i>
          Historial de la solicitud
        </h3>
        <div v-if="!historico.length" class="consulta-empty">
          Aún no hay cambios de estado registrados.
        </div>
        <ul v-else class="consulta-timeline">
          <li
            v-for="(item, idx) in historico"
            :key="item.id || idx"
            class="consulta-timeline__item"
          >
            <span class="consulta-timeline__dot"></span>
            <div class="consulta-timeline__content">
              <div class="consulta-timeline__head">
                <strong>{{ item.nEstadoNuevo || labelEstadoNum(item.estado_new) }}</strong>
                <span>{{ formatoFecha(item.created_at || item.fecha) }}</span>
              </div>
              <p v-if="item.observaciones">{{ item.observaciones }}</p>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </UsuariosExtLayout>
</template>

<script>
import axios from "axios";
import UsuariosExtLayout from "../../components/usuariosExt/UsuariosExtLayout.vue";

export default {
  name: "ConsultarSolicitudes",

  components: {
    UsuariosExtLayout,
  },

  data() {
    return {
      numeroSolicitud: "",
      solicitud: null,
      consultando: false,
      errorConsulta: null,
      recaptchaToken: null,
      recaptchaWidgetId: null,
      errors: {
        numeroSolicitud: null,
        recaptcha: null,
      },
      listas: {
        tiposSolicitud: [],
        estados: [],
        tiposDocumento: [],
      },
    };
  },

  computed: {
    estadoNumero() {
      return Number(this.solicitud && this.solicitud.estado);
    },
    labelEstado() {
      if (!this.solicitud) return "";
      if (this.solicitud.nEstado) return this.solicitud.nEstado;
      return this.labelEstadoNum(this.solicitud.estado);
    },
    labelTipo() {
      if (!this.solicitud) return "—";
      if (this.solicitud.nTipoSolicitud) return this.solicitud.nTipoSolicitud;
      const found = this.listas.tiposSolicitud.find(
        (t) => Number(t.numeracion) === Number(this.solicitud.tipo_solicitud)
      );
      return found ? found.descripcion : this.solicitud.tipo_solicitud || "—";
    },
    nombreCompleto() {
      if (!this.solicitud) return "—";
      return `${this.solicitud.nombres || ""} ${this.solicitud.apellidos || ""}`.trim() || "—";
    },
    documentoMostrado() {
      if (!this.solicitud) return "—";
      const tipo =
        this.solicitud.nTipoDocumento ||
        this.labelFromLista(this.listas.tiposDocumento, this.solicitud.tipo_documento);
      const numero = this.solicitud.no_documento || "—";
      return tipo ? `${tipo} ${numero}` : numero;
    },
    ciudadNombre() {
      if (!this.solicitud) return "—";
      if (this.solicitud.ciudad && this.solicitud.ciudad.nombre) {
        return this.solicitud.ciudad.nombre;
      }
      return "—";
    },
    venta() {
      if (!this.solicitud) return null;
      return this.solicitud.venta_tarjeta || this.solicitud.ventaTarjeta || null;
    },
    historico() {
      if (!this.solicitud) return [];
      const h = this.solicitud.historico_estados || this.solicitud.historicoEstados;
      return Array.isArray(h) ? h : [];
    },
    observacionTexto() {
      if (!this.solicitud) return "";
      return this.formatearObservacion(this.solicitud.observacion);
    },
    detalleEntrega() {
      const venta = this.venta;
      if (!venta) return "";
      const tipo = venta.tipo_entrega;
      if (Number(tipo) === 1 || tipo === "domicilio") {
        return [venta.direccion_entrega, venta.complemento_dir_ent].filter(Boolean).join(" — ");
      }
      if (Number(tipo) === 2 || tipo === "recoger_punto") {
        const sitios = {
          1: "TABIO · Diagonal 7 # 1-17 Local 10",
          2: "TENJO · Carrera 2 # 4-55 Local 114 – Centro Comercial Achua",
          3: "CAJICÁ · Diagonal 1 # 14-31",
          4: "SOPÓ · Carrera 3 # 1-35",
        };
        return sitios[Number(venta.cod_sitio_entrega)] || "";
      }
      return "";
    },
    tarjetaExterno() {
      const t = this.solicitud && this.solicitud.tarjeta;
      return t && t.no_externo ? t.no_externo : null;
    },
    fechaSolicitud() {
      return this.formatoFecha(this.solicitud && (this.solicitud.created_at || this.solicitud.fecha_solicitud));
    },
    estadoClase() {
      const n = this.estadoNumero;
      if (n === 3) return "ok";
      if (n === 2) return "proceso";
      if (n === 4 || n === 5) return "alerta";
      return "pendiente";
    },
    estadoIcono() {
      const map = {
        1: "fas fa-hourglass-half",
        2: "fas fa-sync-alt",
        3: "fas fa-check-circle",
        4: "fas fa-times-circle",
        5: "fas fa-ban",
      };
      return map[this.estadoNumero] || "fas fa-clipboard-list";
    },
    labelEstadoVenta() {
      const venta = this.venta;
      if (!venta) return "—";
      if (venta.nEstado) return venta.nEstado;
      const map = {
        1: "Pendiente",
        2: "En proceso",
        3: "Pagada",
        4: "Rechazada",
      };
      return map[Number(venta.estado)] || venta.estado || "—";
    },
    labelTipoEntrega() {
      const venta = this.venta;
      if (!venta) return "—";
      const tipo = venta.tipo_entrega;
      if (Number(tipo) === 1 || tipo === "domicilio") return "Domicilio";
      if (Number(tipo) === 2 || tipo === "recoger_punto" || tipo === "punto") {
        return "Recoger en punto";
      }
      return tipo || "—";
    },
    pasosEstado() {
      const actual = this.estadoNumero;
      const rechazada = actual === 4 || actual === 5;
      return [
        {
          id: 1,
          label: "Recibida",
          icon: "fas fa-inbox",
          done: actual >= 1,
          current: actual === 1,
        },
        {
          id: 2,
          label: "En proceso",
          icon: "fas fa-cogs",
          done: actual >= 2 && !rechazada,
          current: actual === 2,
        },
        {
          id: 3,
          label: rechazada ? this.labelEstado : "Completada",
          icon: rechazada ? "fas fa-times" : "fas fa-flag-checkered",
          done: actual === 3,
          current: actual === 3 || rechazada,
          rejected: rechazada,
        },
      ];
    },
  },

  mounted() {
    this.cargarListas();
    this.loadRecaptcha();
  },

  beforeDestroy() {
    if (this.recaptchaWidgetId !== null && window.grecaptcha) {
      try {
        window.grecaptcha.reset(this.recaptchaWidgetId);
      } catch (e) {
        /* ignore */
      }
    }
  },

  methods: {
    soloNumeros() {
      this.numeroSolicitud = String(this.numeroSolicitud || "").replace(/\D/g, "");
      this.errors.numeroSolicitud = null;
    },

    labelFromLista(lista, valor) {
      const found = (lista || []).find((i) => Number(i.numeracion) === Number(valor));
      return found ? found.descripcion : "";
    },

    labelEstadoNum(valor) {
      const found = this.listas.estados.find((e) => Number(e.numeracion) === Number(valor));
      if (found) return found.descripcion;
      const map = {
        1: "Pendiente",
        2: "En proceso",
        3: "Completada",
        4: "Rechazada",
        5: "Cancelada",
      };
      return map[Number(valor)] || valor || "—";
    },

    formatoFecha(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString("es-CO", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatoPesos(value) {
      const n = Number(value);
      if (Number.isNaN(n)) return "—";
      return n.toLocaleString("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
      });
    },

    formatearObservacion(observacion) {
      if (observacion == null || observacion === "") return "";
      if (typeof observacion === "object") {
        return this.textoDesdeObjeto(observacion);
      }
      const raw = String(observacion).trim();
      if (!raw) return "";
      if (raw.startsWith("{") || raw.startsWith("[")) {
        try {
          return this.textoDesdeObjeto(JSON.parse(raw)) || raw;
        } catch (e) {
          return raw;
        }
      }
      return raw;
    },

    textoDesdeObjeto(value) {
      if (value == null) return "";
      if (typeof value !== "object") return String(value);
      if (Array.isArray(value)) {
        return value
          .map((entry) => {
            if (entry == null) return "";
            if (typeof entry !== "object") return String(entry);
            const label = entry.label || entry.key || entry.campo || entry.nombre || "";
            const val = entry.value != null ? entry.value : entry.valor;
            return label ? `${label}: ${val}` : String(val || "");
          })
          .filter(Boolean)
          .join(" · ");
      }
      return Object.keys(value)
        .map((key) => `${key}: ${value[key]}`)
        .join(" · ");
    },

    async cargarListas() {
      try {
        const [tipos, estados, docs] = await Promise.all([
          axios.get("/api/lista/48"),
          axios.get("/api/lista/49"),
          axios.get("/api/lista/2"),
        ]);
        this.listas.tiposSolicitud = tipos.data || [];
        this.listas.estados = estados.data || [];
        this.listas.tiposDocumento = docs.data || [];
      } catch (e) {
        /* Las etiquetas de la solicitud cubren el fallback */
      }
    },

    validar() {
      this.errors.numeroSolicitud = null;
      this.errors.recaptcha = null;
      this.errorConsulta = null;

      if (!this.numeroSolicitud || !String(this.numeroSolicitud).trim()) {
        this.errors.numeroSolicitud = "Ingrese el número de solicitud";
      } else if (!/^\d+$/.test(String(this.numeroSolicitud).trim())) {
        this.errors.numeroSolicitud = "El número de solicitud solo debe contener dígitos";
      }

      if (!this.recaptchaToken) {
        this.errors.recaptcha = "Complete la verificación reCAPTCHA para continuar";
      }

      return !this.errors.numeroSolicitud && !this.errors.recaptcha;
    },

    async consultar() {
      if (!this.validar()) {
        this.$swal({
          icon: "warning",
          title: "Faltan datos",
          text: "Ingrese el número de solicitud y complete el reCAPTCHA",
          confirmButtonColor: "#05A85A",
        });
        return;
      }

      this.consultando = true;
      this.solicitud = null;
      this.errorConsulta = null;

      try {
        const response = await axios.get(
          `/api/solicitudesUserTarjeta/${this.numeroSolicitud.trim()}`,
          {
            params: { recaptcha_token: this.recaptchaToken },
          }
        );
        this.solicitud = response.data;
        this.resetRecaptcha();
        this.$nextTick(() => {
          const el = this.$el.querySelector(".consulta-resultado");
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      } catch (error) {
        const status = error.response && error.response.status;
        const data = error.response && error.response.data;
        if (status === 404) {
          this.errorConsulta = "No encontramos una solicitud con ese número. Verifique e intente de nuevo.";
        } else if (status === 422) {
          this.errorConsulta =
            (data && (data.error || data.message)) ||
            "La verificación reCAPTCHA no es válida. Vuelva a marcarla.";
          this.resetRecaptcha();
        } else {
          this.errorConsulta =
            (data && (data.message || data.error)) ||
            "Ocurrió un error al consultar. Intente más tarde.";
        }
      } finally {
        this.consultando = false;
      }
    },

    limpiar() {
      this.numeroSolicitud = "";
      this.solicitud = null;
      this.errorConsulta = null;
      this.errors.numeroSolicitud = null;
      this.errors.recaptcha = null;
      this.resetRecaptcha();
    },

    resetRecaptcha() {
      this.recaptchaToken = null;
      if (this.recaptchaWidgetId !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(this.recaptchaWidgetId);
        } catch (e) {
          /* ignore */
        }
      }
    },

    loadRecaptcha() {
      if (window.grecaptcha && window.grecaptcha.render) {
        this.renderRecaptcha();
        return;
      }

      const script = document.createElement("script");
      script.src =
        "https://www.google.com/recaptcha/api.js?onload=vueRecaptchaLoadedConsulta&render=explicit";
      script.async = true;
      script.defer = true;

      window.vueRecaptchaLoadedConsulta = () => {
        this.renderRecaptcha();
      };

      document.head.appendChild(script);
    },

    renderRecaptcha() {
      this.$nextTick(() => {
        const container = document.getElementById("recaptcha-container-consulta");
        if (!window.grecaptcha || !container) return;
        try {
          if (this.recaptchaWidgetId !== null) return;
          this.recaptchaWidgetId = window.grecaptcha.render("recaptcha-container-consulta", {
            sitekey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
            callback: this.onRecaptchaSuccess,
            "expired-callback": this.onRecaptchaExpired,
            "error-callback": this.onRecaptchaError,
            theme: "light",
            size: "normal",
          });
        } catch (e) {
          console.error("Error al renderizar reCAPTCHA:", e);
        }
      });
    },

    onRecaptchaSuccess(token) {
      this.recaptchaToken = token;
      this.errors.recaptcha = null;
    },

    onRecaptchaExpired() {
      this.recaptchaToken = null;
      this.errors.recaptcha =
        "La verificación reCAPTCHA ha expirado. Por favor, vuelva a verificar.";
      if (this.recaptchaWidgetId !== null && window.grecaptcha) {
        window.grecaptcha.reset(this.recaptchaWidgetId);
      }
    },

    onRecaptchaError() {
      this.recaptchaToken = null;
      this.errors.recaptcha = "Error en la verificación reCAPTCHA. Por favor, intente nuevamente.";
    },
  },
};
</script>

<style scoped lang="scss">
$green-primary: #05a85a;
$green-dark: #0d3d2b;
$teal: #61a198;

.consulta-card__input {
  font-size: 1.15rem;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.consulta-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 1.25rem;
}

.consulta-alert {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 1rem 1.15rem;
  border-radius: 1rem;
  background: #fff;

  i {
    margin-top: 0.15rem;
    font-size: 1.15rem;
  }

  p {
    margin: 0.2rem 0 0;
    font-size: 0.9rem;
  }

  &--error {
    border: 1px solid rgba(192, 57, 43, 0.2);
    color: #7a241c;

    i {
      color: #c0392b;
    }
  }
}

.consulta-status {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 1.25rem 1.35rem;
  border-radius: 1.25rem;
  color: #fff;
  margin-bottom: 1rem;
  box-shadow: 0 10px 28px rgba(13, 61, 43, 0.16);

  &--pendiente {
    background: linear-gradient(135deg, #d39e00 0%, #f0ad4e 100%);
  }

  &--proceso {
    background: linear-gradient(135deg, $teal 0%, #92c0b4 100%);
  }

  &--ok {
    background: linear-gradient(135deg, $green-primary 0%, #8fc74b 100%);
  }

  &--alerta {
    background: linear-gradient(135deg, #c0392b 0%, #e67e22 100%);
  }
}

.consulta-status__icon {
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.consulta-status__kicker {
  display: block;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.9;
}

.consulta-status__title {
  margin: 0.15rem 0 0.2rem;
  font-size: 1.45rem;
  font-weight: 700;
}

.consulta-status__meta {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.95;
}

.consulta-steps {
  list-style: none;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
  margin: 0 0 1rem;
  padding: 0;
}

.consulta-steps__item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0.85rem;
  background: #fff;
  border-radius: 0.85rem;
  border: 1px solid rgba($green-primary, 0.12);
  color: #6b8a7a;
}

.consulta-steps__dot {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e3f1e9;
  font-size: 0.75rem;
}

.consulta-steps__item.is-done,
.consulta-steps__item.is-current {
  color: $green-dark;
  border-color: rgba($green-primary, 0.28);
}

.consulta-steps__item.is-done .consulta-steps__dot,
.consulta-steps__item.is-current .consulta-steps__dot {
  background: $green-primary;
  color: #fff;
}

.consulta-steps__item.is-rejected {
  color: #7a241c;
  border-color: rgba(192, 57, 43, 0.3);
}

.consulta-steps__item.is-rejected .consulta-steps__dot {
  background: #c0392b;
  color: #fff;
}

.consulta-detalle {
  margin-bottom: 1rem;
}

.consulta-detalle__title {
  margin: 0 0 1rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: $green-dark;
}

.consulta-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}

.consulta-field {
  background: #f6fbf8;
  border-radius: 0.75rem;
  padding: 0.75rem 0.9rem;
}

.consulta-field__label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $teal;
  margin-bottom: 0.2rem;
}

.consulta-field__value {
  font-size: 0.95rem;
  color: $green-dark;
  word-break: break-word;
}

.consulta-empty {
  color: #6b8a7a;
  font-size: 0.9rem;
  margin: 0;
}

.consulta-timeline {
  list-style: none;
  margin: 0;
  padding: 0 0 0 0.4rem;
}

.consulta-timeline__item {
  position: relative;
  padding: 0 0 1rem 1.25rem;
  border-left: 2px solid rgba($green-primary, 0.18);

  &:last-child {
    border-left-color: transparent;
    padding-bottom: 0;
  }
}

.consulta-timeline__dot {
  position: absolute;
  left: -0.4rem;
  top: 0.25rem;
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 50%;
  background: $green-primary;
}

.consulta-timeline__head {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  color: $green-dark;
  font-size: 0.9rem;

  span {
    color: #6b8a7a;
    font-size: 0.8rem;
  }
}

.consulta-timeline__content p {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: #4a6b5c;
}

#recaptcha-container-consulta {
  transform-origin: center center;
}

@media (max-width: 400px) {
  #recaptcha-container-consulta {
    transform: scale(0.88);
  }

  .consulta-status {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (min-width: 576px) {
  .consulta-steps {
    grid-template-columns: repeat(3, 1fr);
  }

  .consulta-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .consulta-field--full {
    grid-column: 1 / -1;
  }
}
</style>
